import { createContext, useEffect, useState, useContext } from "react";
import { useSymphonyApiService } from "../../../hooks/useSymphonyApi";
import { useChatHistory } from "./ChatHistoryContext";
import { useChat } from "./ChatContext";
import { useUserProfile } from "./UserProfileContext";
import { useSettings } from "./SettingsContext";
import { useNavigate } from "react-router-dom";
import { ToastContext } from "cai-fusion";
import { useMessages } from "./MessageContext";
import { useAssistant } from "./AssistantContext";
import { createConfiguredPlaceholderTemplate } from "../chatTemplates/templateConstants/placeholderTemplate";
import { useTemplatePropertyTracker } from "../chatTemplates/form/templatePropertyTracker";
import env from "../../../configs/envConfig";
import { debugLog } from "../../../configs/loggingConfig";

const NewTemplateContext = createContext();

function NewTemplateProvider({ children }) {
    const apiServiceClient = useSymphonyApiService("maestro", "v3");
    const { defaultAssistant } = useAssistant();
    const { createToast } = useContext(ToastContext);
    const { receiveMessage, setThinkingResponse, messagesRef, setAssistantThinking } = useMessages();
    const { userProfile } = useUserProfile();
    const { models, settings } = useSettings();
    const { dynamicNewChat, loadChats } = useChatHistory();
    const { selectChat, chat} = useChat();
    const [userMembership, setUserMembership] = useState({});
    const canShare = () => userMembership?.membership < 2;
    const membershipLevels = {
        1: "Manager",
        2: "Contribute",
        3: "View Only"
    };



    // The current chat template's ID. This will be null when no template is selected, or when a template is being made.
    const [chatTemplateId, setChatTemplateId] = useState(undefined);
    // The current chat template. This will be used when viewing or editing a chat template.
    const [chatTemplate, setChatTemplate] = useState(undefined);
    // Chat template's quick actions
    const [chatTemplateShortcuts, setChatTemplateShortcuts] = useState([]);
    // The list of available chat templates.
    const [chatTemplates, setChatTemplates] = useState([]);
    // All, every.
    const [allChatTemplates, setAllChatTemplates] = useState([]);
    // Should be toggled when UI needs to show a loading state.
    const [isLoading, setIsLoading] = useState(false);
    const [executeText, setExecuteText] = useState("");

    const [showRFPItem, setShowRFPItem] = useState(true);

    const navigate = useNavigate();

    const {
        propertyChanges,
        originalTemplate,
        setOriginalTemplate,
        trackPropertyChange,
        resetPropertyChanges,
        getPropertyChanges,
        applyPropertyChanges,
        createCleanTemplatePayload,
    } = useTemplatePropertyTracker(apiServiceClient);

    useEffect(() => {
        if (!userProfile) return;
        fetchTemplates();
    }, [userProfile])

    useEffect(() => {
        if (chatTemplate?.id !== chatTemplateId) {
            fetchTemplate(chatTemplateId);
        }
    }, [chatTemplateId])

    useEffect(() => {
        if (!userProfile) return
        if (chatTemplate?.accesses) {
            setUserMembership({ ...chatTemplate?.accesses?.find(user => user.userProfileId === userProfile.userProfileId) })
        } else {
            setUserMembership({ membership: 1 })
        }
    }, [chatTemplate, userProfile])

    const fetchTemplates = async () => {
        debugLog.log("[TEMPLATE] Loading templates...");
        const respTemplates = await apiServiceClient.Templates.getTemplates();

        debugLog.log("[TEMPLATE] Templates recieved.");
        debugLog.log("[TEMPLATE] Templates:", respTemplates);

        // Filter out inactive accesses from each template
        const templatesWithActiveAccesses = respTemplates?.map(template => ({
            ...template,
            accesses: template.accesses?.filter(access => access.isActive !== false) || []
        })) ?? [];

        // Set the list of chat templates to this returned value.
        setChatTemplates(templatesWithActiveAccesses);
        setAllChatTemplates([...templatesWithActiveAccesses]);
    }

    const fetchTemplate = async (templateId) => {
        if (!templateId) {
            setChatTemplate(undefined);
            setOriginalTemplate(null);
            resetPropertyChanges();
            setChatTemplateShortcuts([]);
            return;
        };

        try {
            debugLog.log("[TEMPLATE] Loading template", templateId);
            let resp = await apiServiceClient.Templates.getTemplate(templateId);
            debugLog.log("[TEMPLATE] Template recieved", resp);
            
            let templateWithActiveAccesses = null;
            
            if (resp) {
                // Filter out inactive accesses from the template, this logic will go to the backend
                templateWithActiveAccesses = {
                    ...resp,
                    accesses: resp.accesses?.filter(access => access.isActive !== false) || [],
                    customText: resp.assistant?.systemMessage ?? resp.customText
                };
                
                setChatTemplate(templateWithActiveAccesses);
                // Store original state for change tracking
                setOriginalTemplate({ ...templateWithActiveAccesses });
                resetPropertyChanges();
                try {
                    debugLog.log("[TEMPLATE] Loading quick actions for template", templateId);
                    const quickActions = await apiServiceClient.QuickActions.getQuickActions(templateId);
                    debugLog.log("[TEMPLATE] Quick actions received", quickActions);
                    setChatTemplateShortcuts(quickActions ?? []);
                } catch (quickActionError) {
                    debugLog.log("[TEMPLATE] Error loading quick actions for template", templateId, quickActionError);
                    setChatTemplateShortcuts([]);
                }
            }

            return templateWithActiveAccesses;
        } catch (error) {
            debugLog.log("[TEMPLATE] Error when loading template with ID", templateId);
            navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/templates/`);
        }
    }

    const navigateToTemplateForm = async (templateId = undefined) => {
        if (templateId) {
            setChatTemplateId(templateId);
            // Redundant operation, but this allows us to be sure we have an updated templateObj.
            await fetchTemplate(templateId);
            navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/templates/${templateId}`);
        }
        else {
            preparePlaceholderTemplate();
            navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/templates/new`);
        }
    }

    const preparePlaceholderTemplate = () => {
        const fullPlaceHolderTemplate = createConfiguredPlaceholderTemplate(models, settings, defaultAssistant);
        setChatTemplate({ ...fullPlaceHolderTemplate });
    }

    const leaveTemplateForm = () => {
        navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/templates`);
        setChatTemplateId(undefined);
    }

    // -------------------------------
    // Basic Template Ops
    // -------------------------------

    const deleteFileFromTemplate = async (resourceId) => {
        setIsLoading(true);
        try {
            await apiServiceClient.Templates.deleteFileFromTemplate(chatTemplateId, resourceId);
        } catch (error) {
            debugLog.log("[TEMPLATE] Error when creating a new chat template.", error);
            setChatTemplates([...chatTemplates])
            createToast("An error occurred while creating your template. Please wait some time and try again.", "error");
        } finally {
            setIsLoading(false);
        }
    }

    const addFileToTemplate = async (file) => {
        setIsLoading(true);
        try {
            const result = await apiServiceClient.Templates.addFileToTemplate(chatTemplateId, file);
            if (result) {
                return result;
            } else {
                createToast("An error occurred while creating your template resource. Please wait some time and try again.", "error");
            }
        } catch (error) {
            debugLog.log("[TEMPLATE] Error when creating a new chat template.", error);
            setChatTemplates([...chatTemplates])
            createToast("An error occurred while creating your template. Please wait some time and try again.", "error");
        } finally {
            setIsLoading(false);
        }
    }

    const createTemplate = async (templateObj) => {
        setIsLoading(true);
        try {
            debugLog.log("[TEMPLATE] Attempting to create template from", templateObj);
            
            // Create a clean payload for the backend by removing properties that have separate API endpoints
            const templatePayload = createCleanTemplatePayload(templateObj);
            
            // Handle assistant ID logic
            if (templatePayload.customText === defaultAssistant.systemMessage) {
                templatePayload.assistantId = defaultAssistant.assistantInstructionId
            } else {
                templatePayload.assistantId = null;
            }

            let newTemplate = await apiServiceClient.Templates.createTemplate(templatePayload);
            debugLog.log("[TEMPLATE] Template created successfully. New object:", newTemplate)

            // Apply any tracked property changes (including files, tags, skills, dataStores, and quick actions)
            await applyPropertyChanges(newTemplate.id);

            // Filter out the temporary template and add the new fully assembled template.
            setChatTemplates([newTemplate, ...chatTemplates]);
            createToast("Chat template created successfully.", "success");

            navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/templates`);
            setChatTemplateId(undefined);
        } catch (error) {
            debugLog.log("[TEMPLATE] Error when creating a new chat template.", error);
            setChatTemplates([...chatTemplates])
            createToast("An error occurred while creating your template. Please wait some time and try again.", "error");
        } finally {
            setIsLoading(false);
        }
    }

    const updateTemplate = async (templateObj) => {
        // Similar to the create template, but when adding the returned value to the list of templates, filter out the old one with the ID.
        setIsLoading(true);
        let oldTemplates = [...chatTemplates]
        try {
            // Create a clean payload for the backend by removing properties that have separate API endpoints
            const templatePayload = createCleanTemplatePayload(templateObj);
            
            // Handle assistant ID logic
            if (templatePayload.customText === defaultAssistant.systemMessage) {
                templatePayload.assistantId = defaultAssistant.assistantInstructionId
            } else {
                templatePayload.assistantId = null;
            }

            const ogTemplate = chatTemplates.find(template => template.id === chatTemplateId);
            const isFavorited = ogTemplate?.isFavorited ?? false;

            debugLog.log("[TEMPLATE] Attempting to update template with ID", chatTemplateId, "to", templatePayload);
            let newTemplate = await apiServiceClient.Templates.updateTemplate(chatTemplateId, templatePayload);
            debugLog.log("[TEMPLATE] Template updated successfully. New object:", newTemplate)

            await applyPropertyChanges(chatTemplateId);

            newTemplate.isFavorited = isFavorited;

            // Filter out the temporary template and add the new fully assembled template.
            setChatTemplates([newTemplate, ...chatTemplates.filter((template) => template.id !== templateObj.id)]);
            createToast(`${newTemplate.title} updated successfully.`, "success");

            navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/templates`);
            setChatTemplateId(undefined);
        }
        catch (error) {
            debugLog.log("[TEMPLATE] Error when updating a chat template.", error);
            setChatTemplates(oldTemplates);
            createToast("An error occurred while updating your template. Please wait some time and try again.", "error");
        }
        finally {
            setIsLoading(false);
        }
    }

    const deleteTemplate = async (templateId) => {
        try {
            debugLog.log("[TEMPLATE] Deleting template with ID:", templateId);
            await apiServiceClient.Templates.deleteTemplate(templateId);
            //this is so that the fetch template (single) doesnt get called again for a template that no longer exists
            if (chatTemplateId === templateId) {
                setChatTemplateId(undefined);
                setChatTemplate(undefined);
                setOriginalTemplate(null);
                resetPropertyChanges();
            }

            // Update states
            setChatTemplates([...chatTemplates.filter((template) => template.id !== templateId)])
            setAllChatTemplates([...chatTemplates.filter((template) => template.id !== templateId)])
        } catch (error) {
            debugLog.log("[TEMPLATE] Problem occurred during template deletion.");
        }
    }

    const searchTemplates = async (query, silent = false) => {
        // Handling the event of an empty search query
        if (!query || !query.trim()) {
            setChatTemplates(allChatTemplates);
            return;
        }

        // Setting loading state for UI loading components
        if (!silent) setIsLoading(true);

        try {
            debugLog.log("[TEMPLATE] Searching templates with query", query);

            // Convert query to lowercase to ensure case-insensitive search
            let lowerCaseQuery = query.toLowerCase();

            // Filter chatTemplates based on name or description containing the query
            const filteredTemplates = allChatTemplates.filter(template => {
                const titleMatch = template.title.toLowerCase().includes(lowerCaseQuery);
                const descriptionMatch = template.description.toLowerCase().includes(lowerCaseQuery);
                return titleMatch || descriptionMatch;
            });
            // Set the filtered templates
            setChatTemplates(filteredTemplates);

            // Check RFP to see if it matches the search criteria.
            const RFPTitle = "Perform a Standard RFP Review"
            const RFPDescription = "Analyze an RFP and get a first review on the document's details."

            setShowRFPItem(RFPTitle.toLowerCase().includes(lowerCaseQuery) || RFPDescription.toLowerCase().includes(lowerCaseQuery));

            debugLog.log("[TEMPLATE] Results:", filteredTemplates);
        } catch (error) {
            handleApiError("Error searching templates.", error);
        } finally {
            setIsLoading(false);
        }
    }

    const renameTemplate = async (templateId, newTitle) => {
        if (!templateId || !newTitle?.trim()) {
            debugLog.warn("[TEMPLATE] Invalid template ID or title for rename operation");
            return;
        }

        setIsLoading(true);
        try {
            debugLog.log("[TEMPLATE] Renaming template with ID", templateId, "to", newTitle);
            
            // Find the current template to preserve other properties
            const currentTemplate = chatTemplates.find(template => template.id === templateId);
            if (!currentTemplate) {
                throw new Error("Template not found");
            }

            // Create a complete payload with the updated title, preserving all other properties
            const renamePayload = {
                ...currentTemplate, 
                title: newTitle.trim() 
            };

            const updatedTemplate = await apiServiceClient.Templates.updateTemplate(templateId, renamePayload);
            debugLog.log("[TEMPLATE] Template renamed successfully:", updatedTemplate);

            // Update the template in the local state
            const updatedTemplates = chatTemplates.map(template => 
                template.id === templateId 
                    ? { ...template, title: newTitle.trim() }
                    : template
            );
            setChatTemplates(updatedTemplates);
            setAllChatTemplates(updatedTemplates);

            // If this is the currently selected template, update it too
            if (chatTemplateId === templateId) {
                setChatTemplate(prev => prev ? { ...prev, title: newTitle.trim() } : prev);
            }

            createToast(`Template renamed to "${newTitle.trim()}" successfully.`, "success");
        } catch (error) {
            debugLog.error("[TEMPLATE] Error renaming template:", error);
            handleApiError("An error occurred while renaming your template. Please try again.", error);
        } finally {
            setIsLoading(false);
        }
    };

    const executeTemplate = async () => {
        if (!chatTemplate?.id) {
            debugLog.warn("[TEMPLATE] No template selected for execution");
            return;
        }

        try {
            debugLog.log("[TEMPLATE] Executing current template:", chatTemplate);
            setExecuteText("Creating a chat for your template");
            
            const session = await apiServiceClient.Templates.createTemplateSession(
                chatTemplate.id, 
                chatTemplate.messageComponents
            );
            
            if (!session?.chatId) {
                throw new Error("Failed to create template session - no chat ID returned");
            }

            await Promise.all([
                selectChat(session.chatId),
                //dynamicNewChat is to add chat to the chat history, a more meaningful name could be used
                // i.e. addChatToHistory
                dynamicNewChat(session),
                addTemplateFilesToChat(session.chatId)
            ]);

            setExecuteText("");
            setAssistantThinking(true);
            setThinkingResponse(session.messages);
            
            const response = await apiServiceClient.Templates.executeSession(
                chatTemplate.id, 
                session.chatId
            );
            
            receiveMessage(response);

            await loadChats(true);
            
        } catch (error) {
            debugLog.error(`[TEMPLATE] Failed to execute template:`, error);
            handleApiError("Failed to execute template.", error);
        } finally {
            setAssistantThinking(false);
            setExecuteText("");
        }
    }

    // To handle file uploads for template execution
    const addTemplateFilesToChat = async (chatId) => {
        try {
            const files = chatTemplate.userAddedFiles || [];
            
            if (files.length === 0) return; 

            setExecuteText("Adding all the proper information to your template's chat");

            await Promise.all(
                files.map(file => 
                    apiServiceClient.Templates.addFileToSession(chatTemplate.id, chatId, file)
                )
            );
            
        } catch (error) {
            debugLog.error(`[TEMPLATE] Failed to upload files:`, error);
            handleApiError("Failed to add files to template chat.", error);
        }
    }

    // -------------------------------
    // Secondary Template Ops
    // -------------------------------

    // Toggle the favorite status of a template.
    const toggleFavoriteTemplate = async (templateId, isFavorited) => {
        try {
            let currTemplate = chatTemplates.find((template) => template.id === templateId);
            debugLog.log("[TEMPLATE] Toggling favorite for ID", templateId, "to", !isFavorited);

            await apiServiceClient.Templates.toggleFavoriteTemplate(templateId);

            currTemplate.isFavorited = !currTemplate.isFavorited;
            // Proc update to the template list to update rendering
            setChatTemplates([...chatTemplates]);
            //createToast("Favorite status updated successfully.", "success");
        } catch (error) {
            handleApiError("Error toggling favorite status.", error);
        }
    }

    const isMember = async (user) => {
        debugLog.log("[DATASTORE] " + user.identifier + " being checked for membership");

        const membership = chatTemplate.accesses.find((x) => x.userProfileId === user?.userProfileId);
        return membership;
    }


    // Adds a user to a template.
    const shareTemplate = async (user) => {
        if (!chatTemplateId) {
            debugLog.error("[TEMPLATE] NO TEMPLATE HERE DUDE");
            return;
        }

        if (user) {
            try {
                debugLog.log("[TEMPLATE] Sharing template with a user.")
                //sharing will always result in 3 membership, the user can then update access to higher levels
                const resp = await apiServiceClient.Templates.shareTemplate(chatTemplateId, user, 3); 
                setChatTemplate({ ...chatTemplate, accesses: [...chatTemplate.accesses, resp] })

                // Updating the list of templates to match the access update, so that the Shared popup can show if needed.
                let tempTemplate = chatTemplates.find((template) => template.id === chatTemplateId);
                tempTemplate.accesses = [...tempTemplate.accesses, resp]
                tempTemplate.isShared = tempTemplate.accesses.length > 1 // Don't forget the owner of the template!
                setChatTemplates([...chatTemplates])

                createToast("Template shared successfully.", "success");
            } catch (error) {
                handleApiError("Error sharing Template.", error);
            } finally {
                setIsLoading(false);
            }
        }
    }


    const updateTemplateAccess = async (userId, membership) => {
        debugLog.log("[TEMPLATES] test")
        setIsLoading(true);
        try {
            const updatedAccess = await apiServiceClient.Templates.updateAccess(chatTemplateId, userId, membership);
            
            // Replace the specific access object with the updated one returned from the server
            const updatedAccesses = chatTemplate.accesses.map(access => 
                access.userProfileId === updatedAccess.userProfileId ? updatedAccess : access
            );
            setChatTemplate({ ...chatTemplate, accesses: updatedAccesses });

            // Updating the list of templates to match the access update
            let tempTemplate = chatTemplates.find((template) => template.id === chatTemplateId);
            tempTemplate.accesses = updatedAccesses;
            setChatTemplates([...chatTemplates]);

            // This operation does not change the number of shared users, so this should not need a visual update.
            createToast("Template access updated successfully", "success");
        } catch (error) {
            handleApiError("Error updating Template access", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Removes a user's access to a data store.
    const removeTemplateAccess = async (userId) => {
        setIsLoading(true);
        try {
            const removedAccess = await apiServiceClient.Templates.removeAccess(chatTemplateId, userId.userProfileId);
            
            // Remove the specific access object that was returned from the server
            const updatedAccesses = chatTemplate.accesses.filter(access => access.userProfileId !== removedAccess.userProfileId);
            setChatTemplate({ ...chatTemplate, accesses: updatedAccesses })

            // Updating the list of templates to match the access update, so that the Shared popup can hide if needed.
            let tempTemplate = chatTemplates.find((template) => template.id === chatTemplateId);
            tempTemplate.accesses = updatedAccesses;
            tempTemplate.isShared = tempTemplate.accesses.length > 1 // Don't forget the owner of the template!
            setChatTemplates([...chatTemplates])

            createToast("User access removed successfully", "success");
        } catch (error) {
            handleApiError("Error removing user access", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Creates a new template shortcut and adds it to the list
    const createTemplateShortcut = (name, prompt) => {
        const newQuickAction = { name, prompt, isEdited: true };
        setChatTemplateShortcuts([newQuickAction, ...chatTemplateShortcuts]);
        trackPropertyChange('quickActions', 'added', [newQuickAction]);
    }

    // Editing a currently existing template shortcut.
    const editTemplateShortcut = (name, prompt, index) => {
        let tempShortcuts = [...chatTemplateShortcuts];
        tempShortcuts[index] = { ...tempShortcuts[index], name, prompt, isEdited: true };
        setChatTemplateShortcuts([...tempShortcuts]);
        
        if (tempShortcuts[index].id) {
            // Existing quick action - track as update
            trackPropertyChange('quickActions', 'updated', [tempShortcuts[index]]);
        } else {
            // New quick action - track as added
            trackPropertyChange('quickActions', 'added', [tempShortcuts[index]]);
        }
    }

    // Deletes a template shortcut from the list.
    const deleteTemplateShortcut = (index) => {
        const shortcut = chatTemplateShortcuts[index];
        if (shortcut.id) {
            // If the shortcut already existed beforehand, mark it for deletion.
            let tempShortcuts = [...chatTemplateShortcuts];
            tempShortcuts[index].isDeleted = true;
            setChatTemplateShortcuts([...tempShortcuts]);
            trackPropertyChange('quickActions', 'removed', [shortcut]);
        } else {
            // If it didn't exist before, we can just remove it.
            setChatTemplateShortcuts([...chatTemplateShortcuts.slice(0, index), ...chatTemplateShortcuts.slice(index + 1)]);
            // No need to track since it was never added
        }
    }

    // TODO: Delete all shortcuts in a template when it's deleted :D
    
    // Helper function for printing API errors.
    const handleApiError = (message, error) => {
        debugLog.error(message, error);
        createToast(message, "error");
        setIsLoading(false);
    };

    return (
        <NewTemplateContext.Provider
            value={{
                // States
                allChatTemplates,
                chatTemplate,
                setChatTemplate,
                chatTemplates,
                chatTemplateShortcuts,
                isLoading,
                setIsLoading,
                executeText,
                chatTemplateId,
                setChatTemplateId,
                membershipLevels,
                isMember,
                canShare,
                showRFPItem,
                // Functions
                navigateToTemplateForm,
                preparePlaceholderTemplate,
                leaveTemplateForm,
                createTemplate,
                fetchTemplate,
                updateTemplate,
                deleteTemplate,
                searchTemplates,
                executeTemplate,
                toggleFavoriteTemplate,
                shareTemplate,
                updateTemplateAccess,
                removeTemplateAccess,
                deleteFileFromTemplate,
                addFileToTemplate,
                createTemplateShortcut,
                editTemplateShortcut,
                deleteTemplateShortcut,
                renameTemplate,
                
                // Change tracking
                propertyChanges,
                trackPropertyChange,
                resetPropertyChanges,
                getPropertyChanges,
                applyPropertyChanges,
                originalTemplate,                
                createCleanTemplatePayload,
            }}
        >
            {children}
        </NewTemplateContext.Provider>
    );
}

// Hook to use the NewTemplateContext in a component
function useNewTemplate() {
    const context = useContext(NewTemplateContext);
    if (context === undefined) {
        throw new Error(
            "useNewTemplate must be used within a NewTemplateProvider"
        );
    }
    return context;
}

export { NewTemplateProvider, useNewTemplate };